<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, ButtonGroup, Container, Row, TabContent, TabPane, Tooltip } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import { ActivityHistory } from '@client/components/activity-history';
  import { AudienceInsights } from '@client/components/audience-insights';
  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import { AssetsService } from '@client/services/assets';

  import { generateAttSessions, updateTimelineData } from '@client/utils/document-report';

  import { getDateFromToday } from '@common/utils/dates';

  import Chart from './attachment-summary/metrics';
  import { DocumentEditForm } from './shared';
  import { SlideAnalytics } from './slide-analytics';

  const navigate = useNavigate();

  export let data = {};
  export let loading = false;
  export let invalidRulename = false;

  let deleteConfirmation = '';
  let history = [];
  let showDeleteConfirmation = false;
  let analyticsData = {};
  let slideData = {};
  let days = 30;
  let dateFormat = 'MMMM d, yyyy';
  let datesFormatted =
    formatDate(getDateFromToday(days - 1), dateFormat) + ' - ' + formatDate(getDateFromToday(0), dateFormat);

  let metrics = {
    sent: [],
    viewed: [],
    downloaded: []
  };

  const colors = {
    sent: '#43A0FC',
    viewed: '#D9BC61',
    downloaded: '#92E2C0'
  };

  const lookbackOptions = [
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 30, time: $format('label.DAYS') } }),
      value: 30
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 60, time: $format('label.DAYS') } }),
      value: 60
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 90, time: $format('label.DAYS') } }),
      value: 90
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 6, time: $format('label.MONTHS') } }),
      value: 180
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 1, time: $format('label.YEAR') } }),
      value: 365
    },
    {
      label: $format('label.DATES_ALL_TIME'),
      value: 365 * 10
    }
  ];

  const onLookbackChange = async (value) => {
    days = value;
    datesFormatted =
      formatDate(getDateFromToday(days - 1), dateFormat) + ' - ' + formatDate(getDateFromToday(0), dateFormat);

    await fetchDocumentMetrics();
  };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const confirmDeletion = async () => {
    loading = true;

    const response = await AssetsService.deleteDocument(data);

    loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.RULENAME_DELETE', { values: { rulename: data.rulename } }), 'error');

      return;
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: data.rulename } }));

    navigate('/manage/assets/documents');
  };

  const toggleConfirmation = () => (showDeleteConfirmation = !showDeleteConfirmation);

  const generateMetricDataset = (series, label, color) => {
    if (series?.length) {
      return {
        datasets: [
          {
            type: 'line',
            label,
            backgroundColor: color + '10',
            borderColor: color,
            pointColor: color,
            fill: true,
            tension: 0.3,
            data: series.map(([x, y]) => ({ x, y }))
          }
        ]
      };
    }

    return null;
  };

  const fetchDocumentMetrics = async () => {
    loading = true;

    const response = await AssetsService.getDocumentMetrics(data._id, data.hash, days);

    analyticsData = updateTimelineData(response);

    metrics.sent = generateMetricDataset(analyticsData.sentSeries, $format('label.DELIVERIES'), colors.sent);
    metrics.viewed = generateMetricDataset(analyticsData.viewedSeries, $format('label.VIEWS'), colors.viewed);
    metrics.downloaded = generateMetricDataset(
      analyticsData.downloadedSeries,
      $format('label.DOWNLOADS'),
      colors.downloaded
    );

    slideData = generateAttSessions(response);
    history = response;

    loading = false;
  };

  onMount(async () => {
    await fetchDocumentMetrics();
  });
</script>

<div id="edit-document">
  <Container fluid>
    <Row>
      <DocumentEditForm bind:data bind:invalidRulename />

      <div class="section">
        <div class="heading">{$format('label.INSIGHTS')}</div>
        <div class="report">
          <div class="range">
            <div><Icon name="calendar" /> {datesFormatted}</div>
            <ButtonGroup size="sm">
              {#each lookbackOptions as option}
                <Button active={days === option.value} on:click={() => onLookbackChange(option.value)}>
                  {option.label}
                </Button>
              {/each}
            </ButtonGroup>
          </div>
          <div class="charts-layout">
            <div class="chart">
              <div class="chart-metric">
                <div class="chart-metric-total">{(analyticsData?.sentTotal || 0).toLocaleString()}</div>
                <div class="chart-metric-label">
                  <span>{$format('label.DELIVERIES')}</span>
                  <Icon name="help-circle" id="tooltip-campaign-deliveries" />
                  <Tooltip target="tooltip-campaign-deliveries" placement="top">
                    {$format('label.TOOLTIP.SUMMATION_CHART', {
                      values: {
                        type: $format('label.DOCUMENT').toLowerCase(),
                        action: $format('label.SENT').toLowerCase()
                      }
                    })}
                  </Tooltip>
                </div>
              </div>
              <div class="chart-wrap">
                <Chart bind:data={metrics.sent} />
              </div>
            </div>
            <div class="chart">
              <div class="chart-metric">
                <div class="chart-metric-total">
                  {(analyticsData?.viewedTotal || 0).toLocaleString()}
                </div>
                <div class="chart-metric-label">
                  <span>{$format('label.VIEWS')}</span>
                  <Icon name="help-circle" id="tooltip-campaign-views" />
                  <Tooltip target="tooltip-campaign-views" placement="top">
                    {$format('label.TOOLTIP.SUMMATION_CHART', {
                      values: {
                        type: $format('label.DOCUMENT').toLowerCase(),
                        action: $format('label.VIEWED').toLowerCase()
                      }
                    })}
                  </Tooltip>
                </div>
              </div>
              <div class="chart-wrap">
                <Chart bind:data={metrics.viewed} />
              </div>
            </div>
            <div class="chart">
              <div class="chart-metric">
                <div class="chart-metric-total">
                  {(analyticsData?.downloadedTotal || 0).toLocaleString()}
                </div>
                <div class="chart-metric-label">
                  <span>{$format('label.DOWNLOADS')}</span>
                  <Icon name="help-circle" id="tooltip-campaign-clicks" />
                  <Tooltip target="tooltip-campaign-clicks" placement="top">
                    {$format('label.TOOLTIP.SUMMATION_CHART', {
                      values: {
                        type: $format('label.DOCUMENT').toLowerCase(),
                        action: $format('label.DOWNLOADED').toLowerCase()
                      }
                    })}
                  </Tooltip>
                </div>
              </div>
              <div class="chart-wrap">
                <Chart bind:data={metrics.downloaded} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {#if Object.keys(slideData).length || history.length}
        <div class="section">
          <div class="heading">{$format('label.REPORTS')}</div>
          <div class="activity-details">
            <div class="activity-details-tabs">
              <TabContent>
                {#if Object.keys(slideData).length}
                  <TabPane tabId="engagement" tab={$format('label.ENGAGEMENT')}>
                    <SlideAnalytics bind:data={slideData} bind:hash={data.hash} />
                  </TabPane>
                {/if}
                {#if history.length}
                  <TabPane tabId="history" tab={$format('label.HISTORY')}>
                    <ActivityHistory bind:data={history} />
                  </TabPane>
                  <TabPane tabId="audience" tab={$format('label.AUDIENCE')}>
                    <AudienceInsights row={{ activity: history }} />
                  </TabPane>
                {/if}
              </TabContent>
            </div>
          </div>
        </div>
      {/if}

      <div class="section">
        <div class="delete-group">
          <h2>{$format('label.DELETE')}</h2>
          <p>{@html $format('label.DELETE_WARNING', { values: { type: $format('label.DOCUMENT').toLowerCase() } })}</p>
          {#if showDeleteConfirmation}
            <div class="delete-confirmation">
              <input
                type="text"
                bind:value={deleteConfirmation}
                placeholder={$format('label.PLACEHOLDER.DELETE', {
                  values: {
                    type: $format('label.DOCUMENT').toLowerCase(),
                    param: $format('label.NAME').toLowerCase()
                  }
                })}
              />
              <Button color="danger" disabled={deleteConfirmation !== data.rulename} on:click={confirmDeletion}
                >{$format('label.DELETE')}</Button
              >
              <Button color="secondary" on:click={toggleConfirmation}>{$format('label.CANCEL')}</Button>
            </div>
            <small
              >{$format('label.DELETE_REQUIREMENT', {
                values: {
                  type: $format('label.DOCUMENT').toLowerCase(),
                  param: $format('label.RULENAME').toLowerCase()
                }
              })}</small
            >
          {:else}
            <Button color="danger" on:click={toggleConfirmation}>{$format('label.DELETE')}</Button>
          {/if}
        </div>
      </div>
    </Row>
  </Container>
</div>

<style lang="scss" src="./edit-document.scss"></style>
